import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  IconButtonProps,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ArrowBack,
  ArrowForward,
  Check,
  CheckCircle,
  Delete,
  Edit,
  FiberManualRecordRounded,
  RadioButtonUnchecked,
  Schedule,
  SettingsBackupRestore,
} from '@material-ui/icons';
import qs from 'qs';
import { format } from 'date-fns';
import { find } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import Scrollbars from 'react-custom-scrollbars';
import { isFirefox } from 'react-device-detect';
import instance from 'api';
import ExcelIcon from 'assets/icons/svg/ExcelIcon';
import PdfIcon from 'assets/icons/svg/PdfIcon';
import Download from 'assets/icons/svg/Download';
import { downloadFile, getFilenameFromContentDisposition, USER_OPERATIONS } from 'helper';
import { IAvailableColumnModel, IAvailableGroupColumnModel, IFilterColumnModel, IOrderColumnModel, RootState } from 'interfaces';
import Table from 'components/Table';
import TableCell from 'components/Table/components/TableCell';
import TableRow from 'components/Table/components/TableRow';
import FileList from 'components/FilesList';
import VisibilityDialog from './components/VisiblityDialog';
import ExpandableFilters from './components/ExpandableFilters';
import { exportExcelReport, exportPDFReport } from './api';
import './styles.scss';

interface IDynamicTableProps {
  pageSize: number;
  pageNo: number;
  defaultValues?: { filters: IAvailableColumnModel[]; columns: IAvailableColumnModel[] };
  getItems: (params: IDynamicTableParams) => Promise<any[]>;
  getAvailableGroupColumns?: () => Promise<IAvailableGroupColumnModel[]>;
  setItemLink: (item: any) => string;
  onClick?: (item: any, itemIndex?: number) => void;
  visibleColumns: IAvailableColumnModel[];
  visibleFilters: IAvailableColumnModel[];
  filterColumns?: IFilterColumnModel[];
  orderColumns?: IOrderColumnModel[];
  baseApiUrl?: string;
  listExcelUrl?: string;
  listExcelFilename?: string;
  listExcelOperations?: (keyof typeof USER_OPERATIONS)[];
  listExcelVisibleColumnsOnly?: boolean;
  listPDFUrl?: string;
  listCountUrl?: string;
  listCountUrlParams?: { [key: string]: string };
  canReset?: boolean;
  onChange?: (
    items: any[],
    visibleColumns: IAvailableColumnModel[],
    visibleFilters: IAvailableColumnModel[],
    filterColumns: IFilterColumnModel[],
    orderColumns: IOrderColumnModel[],
    pageNo: number,
    pageSize: number,
  ) => void;
  actions?: (
    item?: any,
    index?: number,
  ) => {
    icon: React.ReactNode;
    label: string;
    action: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    display?: boolean;
    IconButtonProps?: IconButtonProps;
  }[];
  /**
   * Заголовки колонок действий: одна строка на всю группу либо массив по действию.
   * Не задан — ячейки шапки пустые, как у onDelete/onEdit.
   */
  actionsCaption?: string | string[];
  onAdd?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRemove?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDelete?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRestore?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDownload?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDownloadRender?: () => React.ReactNode;
  onCopyLink?: (item: any) => void;
  onExtendPeriod?: (item: any) => void;
  hasExpandableFilter?: boolean;
  noMarginTop?: boolean;
  excelFilterColumns?: any;
  requiredFields?: string[];
  requiredFilters?: string[];
  hideFilters?: string[];
  hideColumns?: string[];
  filterConfigName?: string;
  fixedColumn?: IAvailableColumnModel;
  updating?: boolean;
  onEdit?: (item: any, index?: number, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  forceUpdating?: boolean;
  stopForceUpdating?: () => void;
  isSelectable?: boolean;
  onSelectItem?: (item: any) => void;
  selectedItems?: string[];
  /**
   * Чем идентифицируется строка в `selectedItems`.
   *
   * По умолчанию — `item.applicationNumber`: раньше это было захардкожено, и
   * выбор строк работал только на реестрах заявок. Реестрам с другим ключом
   * (например, `userId`) нужно передать свой.
   */
  getRowKey?: (item: any) => string;
  /**
   * Можно ли выбрать строку. Невыбираемые рисуются серой точкой.
   *
   * По умолчанию воспроизводит прежнее захардкоженное правило для заявок:
   * закрытые и отменённые клиентом выбрать нельзя.
   */
  isRowSelectable?: (item: any) => boolean;
  /**
   * Обработчик чекбокса в шапке. Передан — шапка становится кликабельной
   * («выбрать всё на странице»); не передан — остаётся прежняя неактивная
   * иконка, чтобы не менять поведение существующих реестров.
   */
  onSelectAllItems?: (pageItems: any[], nextSelected: boolean) => void;
  isEditable?: boolean;
  /** CSS-класс строки в зависимости от данных элемента (например, подсветка непросмотренных заявок) */
  rowClassName?: (item: any) => string | undefined;
}

export interface IDynamicTableParams {
  filterColumns?: IFilterColumnModel[];
  orderColumns?: IOrderColumnModel[];
  selectColumns: string[];
  hiddenColumns?: string[];
  pageNo: number;
  pageSize: number;
  title?: string;
}

// Плоский список имён колонок, которые реально существуют на бэкенде.
const collectKnownColumnNames = (availableGroupColumns: IAvailableGroupColumnModel[]): Set<string> => {
  const knownColumnNames = new Set<string>();

  availableGroupColumns.forEach((group) => {
    (group.columns || []).forEach((column) => knownColumnNames.add(column.columnName));
  });

  return knownColumnNames;
};

// Массивы живут в useRef, поэтому чистим их на месте: подмена ссылки перезапустила бы
// эффект filterColumnsCurrentChange и потянула бы за собой лишний запрос списка.
const dropUnknownColumns = <T extends { columnName: string }>(
  columns: T[] | undefined,
  isKnown: (columnName: string) => boolean,
): void => {
  if (!columns) return;

  for (let index = columns.length - 1; index >= 0; index--) {
    if (!isKnown(columns[index].columnName)) columns.splice(index, 1);
  }
};

/**
 * Колонки с фото: вместо текста рисуются миниатюры FilesList с лайтбоксом по клику.
 * photoFileId - одиночный ключ фото отметки в реестре геоконтроля (SERVICE-5420).
 */
const PHOTO_COLUMNS = new Set(['photoList', 'photoListBefore', 'photoListAfter', 'photoFileId']);

/** Ключи фото ячейки списком: одиночный ключ приходит строкой, пустое значение - без фото. */
const toPhotoKeys = (value: string | string[] | null | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

/** Соединяет visibleFilters из query-строки и из props: query главнее. */
const getVisibleFilters = (queried: IAvailableColumnModel[], fromProps: IAvailableColumnModel[]) => {
  if (!queried) return fromProps || [];
  const filteredOut = fromProps.filter(
    (visibleFilter) => !queried.some((queriedFilter) => queriedFilter.columnName === visibleFilter.columnName),
  );
  return [...filteredOut, ...queried];
};

/**
 * Сколько колонок занимают действия.
 *
 * Набор берём с первой строки: до загрузки её нет, тогда резервируем одну ячейку —
 * иначе шапка и тело таблицы разъезжаются на первом рендере.
 */
const resolveActionColumnCount = (
  actions: IDynamicTableProps['actions'],
  items: any[],
): number => {
  if (!actions) return 0;
  if (items.length === 0) return 1;
  return (actions(items[0], 0) || []).filter((action) => action.display !== false).length;
};

/**
 * Подписи колонок действий по порядку.
 *
 * Строкой задаётся подпись только первой колонки — так было до появления второй,
 * и ломать существующие вызовы ради единообразия незачем.
 */
const resolveActionColumnCaptions = (
  caption: string | string[] | undefined,
  count: number,
): (string | undefined)[] =>
  Array.from({ length: count }, (_cell, index) => {
    if (Array.isArray(caption)) return caption[index];
    return index === 0 ? caption : undefined;
  });

/** Иконка состояния строки: выбрана, недоступна для выбора, свободна. */
const selectionIcon = ({ selected, selectable }: { selected: boolean; selectable: boolean }) => {
  if (selected) return <CheckCircle />;
  if (selectable) return <RadioButtonUnchecked />;
  return <FiberManualRecordRounded color="disabled" />;
};

/** Сохранённая конфигурация фильтров реестра: ключ задаёт страница через filterConfigName. */
const readFilterConfig = (filterConfigName?: string): any => {
  const stored = filterConfigName ? localStorage.getItem(filterConfigName) : null;
  return stored ? JSON.parse(stored) : null;
};

/**
 * Состояние чекбокса в шапке. Считаем только по выбираемым строкам текущей страницы:
 * иначе закрытые заявки не давали бы галке стать «выбрано всё».
 */
const pageSelectionState = ({
  pageItems,
  selectedKeys,
  rowKey,
}: {
  pageItems: any[];
  selectedKeys?: string[];
  rowKey: (item: any) => string;
}) => {
  const isSelected = (item: any) => !!selectedKeys?.includes(rowKey(item));

  return {
    allSelected: pageItems.length > 0 && pageItems.every(isSelected),
    someSelected: pageItems.some(isSelected),
  };
};

const DynamicTable: React.FC<IDynamicTableProps> = (props) => {
  const operations = useSelector((state: RootState) => state.operations);
  const canDownloadExcel =
    !props.listExcelOperations ||
    props.listExcelOperations.some((operation) => operations.includes(USER_OPERATIONS[operation]));
  const parsedQuery: any = qs.parse(window.location.search);
  const filterConfigName = props.filterConfigName;
  const filterConfig = readFilterConfig(filterConfigName);
  const hasStoredConfig = Boolean(filterConfig);

  const filterColumns = useRef<IFilterColumnModel[]>(
    filterConfig ? filterConfig.filterColumns : props.filterColumns || [],
  );
  const orderColumns = useRef<IOrderColumnModel[]>(
    filterConfig && filterConfig.orderColumns ? filterConfig.orderColumns : props.orderColumns || [],
  );
  const [pending, setPending] = useState<boolean>(true);
  const [items, setItems] = useState<any[]>([]);
  const [availableGroupColumns, setAvailableGroupColumns] = useState<IAvailableGroupColumnModel[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<IAvailableColumnModel[]>(props.visibleColumns || []);
  const [visibleFilters, setVisibleFilters] = useState<IAvailableColumnModel[]>(
    filterConfig ? getVisibleFilters(filterConfig.visibleFilters, props.visibleFilters) : props.visibleFilters,
  );
  const [pageNo, setPageNo] = useState<number>(parseInt(parsedQuery.p) || props.pageNo);
  const [pageSize, setPageSize] = useState<number>(parseInt(parsedQuery.ps) || props.pageSize);
  const [columnVisibilityDialogOpen, setColumnVisibilityDialogOpen] = useState(false);
  const [filterVisibilityDialogOpen, setFilterVisibilityDialogOpen] = useState(false);
  const [listCount, setListCount] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [excelFileDownloading, setExcelFileDownloading] = useState(false);
  const [PDFFileDownloading, setPDFFileDownloading] = useState(false);

  const {
    onChange: propsOnChange,
    getAvailableGroupColumns: propsGetAvailableColumns,
    getItems: propsGetItems,
    excelFilterColumns: propsExcelFilterColumns,
    forceUpdating: propsForceUpdating,
    stopForceUpdating: propsStopForceUpdating,
    isSelectable: propsIsSelectable,
    onSelectItem: propsOnSelectItem,
    selectedItems: propsSelectedItems,
    onSelectAllItems: propsOnSelectAllItems,
    isEditable: propsIsEditable,
    baseApiUrl = '/api',
  } = props;

  // Дефолты 1:1 воспроизводят прежнее захардкоженное поведение реестров заявок,
  // поэтому существующие потребители (PageApplicationList, PageGroupApplicationList,
  // PageMyApplicationList) не меняются ни на строку.
  const rowKey = props.getRowKey ?? ((item: any) => item.applicationNumber);
  const isRowSelectable =
    props.isRowSelectable ??
    ((item: any) => !['Закрыто', 'Отменена клиентом'].includes(item.statusName));

  const { allSelected: pageAllSelected, someSelected: pageSomeSelected } = pageSelectionState({
    pageItems: items.filter(isRowSelectable),
    selectedKeys: propsSelectedItems,
    rowKey,
  });

  const getParams = useCallback(() => {
    const selectColumns =
      visibleColumns.length > 0 ? visibleColumns.map((visibleColumn) => visibleColumn.columnName) : [];
    if (!!props.requiredFields && props.requiredFields.length > 0) {
      props.requiredFields.forEach((item) => {
        selectColumns.push(item);
      });
    }

    if (!!props.fixedColumn) {
      selectColumns.push(props.fixedColumn.columnName);
    }

    const params: IDynamicTableParams = {
      pageNo,
      pageSize,
      selectColumns: selectColumns,
    };

    const toQueryParams: any = {
      p: pageNo,
      ps: pageSize,
    };

    // ts-ignore
    window.history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${qs.stringify(toQueryParams, {
        addQueryPrefix: true,
      })}`,
    );

    params.filterColumns = [];

    if (filterColumns.current && filterColumns.current.length > 0) {
      for (let i = 0; i < filterColumns.current.length; i++) {
        let item = filterColumns.current[i];
        if (visibleFilters.map((filter) => filter.columnName).indexOf(item.columnName) !== -1) {
          if (item.criteria === 'BETWEEN' && item.filterValues[0] !== '') {
            const dates = [...item.filterValues[0].split('-')];
            if (params.filterColumns) {
              params.filterColumns.push({
                ...item,
                filterValues: [
                  moment(dates[0], 'DD.MM.YYYY').format('YYYY-MM-DD'),
                  moment(dates[1], 'DD.MM.YYYY').format('YYYY-MM-DD'),
                ],
              });
            }
          } else {
            const tempFilterValues: any[] = [];
            let requestItem;
            item.filterValues.forEach((filterValue) => {
              if (filterValue.key) {
                tempFilterValues.push(filterValue.key);
              } else {
                tempFilterValues.push(filterValue);
              }
            });

            requestItem = {
              columnName: item.columnName,
              criteria: item.criteria,
              filterValues: tempFilterValues,
            };

            if (params.filterColumns) {
              params.filterColumns.push({ ...requestItem });
            }
          }
        } else {
          filterColumns.current[i].filterValues = [];
          filterColumns.current.splice(i--, 1);
        }
      }
    }

    if (propsExcelFilterColumns) {
      params.filterColumns.push(propsExcelFilterColumns[0]);
    }

    params.orderColumns = [...orderColumns.current];

    return params;
  }, [
    pageNo,
    pageSize,
    propsExcelFilterColumns,
    visibleColumns,
    visibleFilters,
    props.requiredFields,
    props.fixedColumn,
  ]);

  const getItems = useCallback(() => propsGetItems(getParams()), [propsGetItems, getParams]);

  // Имена колонок, заданные самой страницей. Часть страниц перечисляет колонки литералами
  // или подставляет их из собственного localStorage, и бэкенд про такие имена может не знать,
  // поэтому пришедшее через props не режем никогда — чистим только конфиг из хранилища.
  const propsColumnNames = useCallback(() => {
    const names = new Set<string>();
    const collect = (columns?: { columnName: string }[]) =>
      (columns || []).forEach((column) => names.add(column.columnName));

    collect(props.visibleColumns);
    collect(props.visibleFilters);
    collect(props.filterColumns);
    collect(props.orderColumns);
    (props.requiredFields || []).forEach((columnName) => names.add(columnName));
    if (props.fixedColumn) names.add(props.fixedColumn.columnName);

    return names;
  }, [
    props.visibleColumns,
    props.visibleFilters,
    props.filterColumns,
    props.orderColumns,
    props.requiredFields,
    props.fixedColumn,
  ]);

  const initialize = useCallback(() => {
    if (propsGetAvailableColumns) {
      propsGetAvailableColumns()
        .then((availableColumnsResponse) => {
          setAvailableGroupColumns([...availableColumnsResponse]);

          // Сохранённый в localStorage конфиг мог остаться от версии, где колонка ещё
          // существовала (например удалённые детали логирования у реестра /logs). Такие имена
          // бэкенд не знает, и запрос списка отвечает ошибкой — пересекаем конфиг с ответом
          // сервера до первого getItems(). Пустой ответ считаем сбоем и ничего не режем,
          // иначе разовая ошибка availableGroupColumns снесла бы пользователю все фильтры.
          const knownColumnNames = collectKnownColumnNames(availableColumnsResponse);

          if (hasStoredConfig && knownColumnNames.size > 0) {
            const allowedColumnNames = propsColumnNames();
            const isKnown = (columnName: string) =>
              knownColumnNames.has(columnName) || allowedColumnNames.has(columnName);

            dropUnknownColumns(filterColumns.current, isKnown);
            dropUnknownColumns(orderColumns.current, isKnown);

            const knownVisibleFilters = visibleFilters.filter((filter) => isKnown(filter.columnName));
            if (knownVisibleFilters.length !== visibleFilters.length) setVisibleFilters(knownVisibleFilters);
          }

          getItems()
            .then((itemsResponse: any[]) => {
              if (propsOnChange) {
                propsOnChange(
                  itemsResponse,
                  visibleColumns,
                  visibleFilters,
                  filterColumns.current,
                  orderColumns.current,
                  pageNo,
                  pageSize,
                );
              }
              setItems(itemsResponse);

              // setError(false);
              setPending(false);
            })
            .catch(() => {
              // setError(true);
              setPending(false);
            });
        })
        .catch(() => {
          // setError(true);
          setPending(false);
        });
    }
  }, [
    getItems,
    pageNo,
    pageSize,
    visibleColumns,
    visibleFilters,
    propsOnChange,
    propsGetAvailableColumns,
    hasStoredConfig,
    propsColumnNames,
  ]);

  useEffect(initialize, []);

  const doFilter = useCallback((): void => {
    saveFilterConfigInLocalStorage(filterColumns.current);
    setItems([]);
    setListCount(null);
    // setError(false);
    setPending(true);
    if (propsStopForceUpdating) {
      propsStopForceUpdating();
    }
    getItems()
      .then((itemsResponse) => {
        if (propsOnChange) {
          propsOnChange(
            itemsResponse,
            visibleColumns,
            visibleFilters,
            filterColumns.current,
            orderColumns.current,
            pageNo,
            pageSize,
          );
        }
        setItems(itemsResponse);
        setExpanded(false);
        // setError(false);
        setPending(false);
      })
      .catch(() => {
        // setError(true);
        setPending(false);
        setExpanded(false);
      });
  }, [getItems, pageNo, pageSize, propsOnChange, visibleColumns, visibleFilters, propsStopForceUpdating]); // eslint-disable-line

  const visibleColumnsChange = useCallback(() => {
    if (!pending) {
      doFilter();
    }
  }, [pending, doFilter]);

  useEffect(visibleColumnsChange, [visibleColumns, props.updating]);

  // Force update only data in table, not in filters
  useEffect(() => {
    if (propsForceUpdating) {
      doFilter();
    }
  }, [propsForceUpdating, doFilter]);

  const visibleFiltersChange = useCallback(() => {
    if (!pending) {
      doFilter();
    }
  }, [pending, doFilter]);

  useEffect(visibleFiltersChange, [visibleFilters]);

  const pageSizeChange = useCallback(() => {
    if (!pending) {
      doFilter();
    }
  }, [pending, doFilter]);

  useEffect(pageSizeChange, [pageSize]);

  const pageNoChange = useCallback(() => {
    if (!pending) {
      doFilter();
    }
  }, [pending, doFilter]);

  useEffect(pageNoChange, [pageNo]);

  const onChangeFilterColumns = (newFilterColumns: IFilterColumnModel[]): void => {
    while (filterColumns.current.length > 0) {
      filterColumns.current.pop();
    }

    const filterFilterColumns = newFilterColumns.filter((item) => {
      if (item.criteria === 'BETWEEN') {
        if (item.filterValues[0].length === 21) {
          return { ...item };
        } else {
          return false;
        }
      } else {
        return { ...item };
      }
    });

    filterColumns.current = [...filterFilterColumns];
  };

  const showColumnVisibilityDialog = (): void => {
    setColumnVisibilityDialogOpen(true);
  };

  const closeColumnVisibilityDialog = (): void => {
    setColumnVisibilityDialogOpen(false);
  };

  const saveVisibleColumns = (newVisibleColumns: IAvailableColumnModel[]): void => {
    setVisibleColumns(newVisibleColumns);
    closeColumnVisibilityDialog();
  };

  const showFilterVisibilityDialog = (): void => {
    setFilterVisibilityDialogOpen(true);
  };

  const closeFilterVisibilityDialog = (): void => {
    setFilterVisibilityDialogOpen(false);
  };

  const saveFilterColumns = (newVisibleFilters: IAvailableColumnModel[]): void => {
    setVisibleFilters(newVisibleFilters);
    closeFilterVisibilityDialog();
  };

  const sort = (visibleColumn: IAvailableColumnModel) => () => {
    if (orderColumns.current.length > 0) {
      const currentItem = find(orderColumns.current, (item: IOrderColumnModel) => {
        return item.columnName.valueOf() === visibleColumn.columnName.valueOf();
      });

      if (currentItem) {
        orderColumns.current[0].sortType = orderColumns.current[0].sortType.valueOf() === 'DESC' ? 'ASC' : 'DESC';
      } else {
        while (orderColumns.current.length > 0) {
          orderColumns.current.pop();
        }

        orderColumns.current.push({
          columnName: visibleColumn.columnName,
          sortType: 'DESC',
        });
      }
    } else {
      orderColumns.current.push({
        columnName: visibleColumn.columnName,
        sortType: 'DESC',
      });
    }

    doFilter();
  };

  const handleChange = (e: React.ChangeEvent<{ value: any }>): void => {
    setPageSize(parseInt(e.target.value));
  };

  const formatTimestamp = (text: any, type: string): string => {
    try {
      if (type === 'Timestamp' || type === 'LocalDateTime') {
        return format(moment(text).toDate(), 'dd.MM.yyyy HH:mm:ss');
      } else {
        return format(moment(text).toDate(), 'dd.MM.yyyy');
      }
    } catch (e) {
      return text;
    }
  };

  const getBooleanValues = (value: boolean) => {
    if (value) {
      return 'Да';
    } else if (
      value === null &&
      availableGroupColumns &&
      availableGroupColumns.length &&
      availableGroupColumns[0].name === 'indicator'
    ) {
      // Only for "Чек-лист приема помещений" page
      return '-';
    } else {
      return 'Нет';
    }
  };

  const NUMERIC_TYPES = ['Integer', 'Long', 'BigDecimal', 'Double', 'Float', 'Number', 'int', 'long', 'double', 'float'];

  const isNumericType = (type: string): boolean => {
    return NUMERIC_TYPES.includes(type);
  };

  const formatNumber = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('ru-RU');
  };

  const resetFilterValues = () => {
    const requiredFilters = filterColumns.current.reduce<IFilterColumnModel[]>((acc, curr, index, array) => {
      if (props.requiredFilters && array.map(({ columnName }) => columnName).includes(props.requiredFilters[index]))
        acc.push(curr);
      return acc;
    }, []);
    filterColumns.current = requiredFilters;

    setPending(true);
    setListCount(null);
    setExpanded(false);
    saveFilterConfigInLocalStorage(requiredFilters);
    getItems()
      .then((itemsResponse: any[]) => {
        if (props.onChange) {
          props.onChange(
            itemsResponse,
            visibleColumns,
            visibleFilters,
            requiredFilters,
            orderColumns.current,
            pageNo,
            pageSize,
          );
        }
        setItems(itemsResponse);
        // setError(false);
      })
      .catch(() => {
        // setError(true);
      })
      .finally(() => {
        setPending(false);
      });
  };

  const exportTable2Excel = () => {
    if (props.listExcelUrl) {
      setExcelFileDownloading(true);
      const { selectColumns, filterColumns, ...query } = getParams();
      const hiddenColumns = selectColumns.filter(
        (column) => !visibleColumns.some((visibleColumn) => visibleColumn.columnName === column),
      );
      const params = {
        ...query,
        pageSize: 40000,
        pageNo: 1,
        selectColumns: props.listExcelVisibleColumnsOnly
          ? visibleColumns.map((column) => column.columnName)
          : selectColumns,
        filterColumns,
        ...(baseApiUrl === '/api' && hiddenColumns.length ? { hiddenColumns } : {}),
      };

      exportExcelReport(params, baseApiUrl, props.listExcelUrl)
        .then((response) => {
          downloadFile(
            response.data,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            props.listExcelFilename ?? getFilenameFromContentDisposition(response.headers['content-disposition']),
          );
        })
        .finally(() => setExcelFileDownloading(false));
    }
  };

  const exportTable2PDF = () => {
    if (props.listPDFUrl) {
      setPDFFileDownloading(true);
      const { selectColumns, filterColumns, ...query } = getParams();
      const params = { ...query, pageSize: 40000, pageNo: 1, selectColumns, filterColumns };

      exportPDFReport(params, baseApiUrl, props.listPDFUrl)
        .then((response) => {
          downloadFile(
            response.data,
            'application/pdf',
            getFilenameFromContentDisposition(response.headers['content-disposition']),
          );
          setPDFFileDownloading(false);
        })
        .finally(() => setPDFFileDownloading(false));
    }
  };

  const getListCount = () => {
    if (props.listCountUrl) {
      setPending(true);
      const query = getParams();
      const params = {
        ...props.listCountUrlParams,
        ...query,
        selectColumns: query.selectColumns,
        filterColumns: query.filterColumns,
      };

      // FIXME: Временное решение
      const isBaseApi = baseApiUrl?.match(/^\/api$/);
      (isBaseApi
        ? instance().post(props.listCountUrl, params)
        : instance(true, baseApiUrl).get(props.listCountUrl, {
            params,
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          })
      )
        .then((response: any) => {
          setListCount(response.data);
          setPending(false);
        })
        .catch(() => {
          setPending(false);
        });
    }
  };

  const filterColumnsCurrentChange = useCallback(() => {
    if (filterColumns.current.length && props.hasExpandableFilter) {
      const updated = [...visibleFilters];
      setVisibleFilters(updated);
    }
  }, [props.hasExpandableFilter, visibleFilters]);

  useEffect(filterColumnsCurrentChange, [filterColumns.current]);

  const saveFilterConfigInLocalStorage = (filterColumns: IFilterColumnModel[]) => {
    if (filterConfigName) {
      // save filter config in localstorage by name, except filter-config, because we use it only for specific cases. Check above in local states declaration
      localStorage.setItem(
        filterConfigName,
        JSON.stringify({
          filterColumns,
          visibleColumns,
          visibleFilters,
          orderColumns: orderColumns.current,
          pageNo,
          pageSize,
        }),
      );
    }
  };

  /**
   * Сколько ячеек занимает колонка действий. Экшены рендерятся по ячейке на действие, а шапка
   * про них не знала — строка заголовка выходила короче тела, и колонки визуально разъезжались.
   * Набор берём с первой строки: до загрузки её нет, тогда резервируем одну ячейку.
   */
  const actionColumnCount = resolveActionColumnCount(props.actions, items);
  const actionColumnCaptions = resolveActionColumnCaptions(props.actionsCaption, actionColumnCount);
  const renderSelectionIcon = (item: any) => selectionIcon({
    selected: Boolean(propsSelectedItems?.includes(rowKey(item))),
    selectable: isRowSelectable(item),
  });

  const hasFilters = Boolean(
    visibleFilters
      .filter((item) => !!item.filterCriteriaList.length)
      .filter((filter) => !props.hideFilters?.includes(filter.columnName)).length > 0,
  );

  return (
    <>
      <Grid container direction="column" spacing={2} className="dynamic-table">
        <Grid item container justifyContent="flex-end" xs={12}>
          <ButtonGroup className="dynamic-table-controls">
            {hasFilters && props.canReset && <Button onClick={resetFilterValues}>Очистить фильтры</Button>}
            <Button onClick={showFilterVisibilityDialog}>Настроить фильтр</Button>
            <Button onClick={showColumnVisibilityDialog}>Настроить таблицу</Button>
            <Button
              onClick={() => {
                if (pageNo !== 1) setPageNo(1);
                else doFilter();
              }}
            >
              Применить
            </Button>
          </ButtonGroup>
        </Grid>

        {Boolean(visibleFilters.length) && (
          <Grid item style={{ position: 'relative' }} xs={12}>
            <ExpandableFilters
              className="dynamic-table-filters"
              hideFilters={props.hideFilters}
              filters={visibleFilters}
              doFilter={doFilter}
              baseApiUrl={baseApiUrl}
              setFilterColumns={onChangeFilterColumns}
              filterColumns={filterColumns.current}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          </Grid>
        )}

        <Grid item xs={12} style={{ margin: visibleFilters.length ? '58px 0 0' : 0 }}>
          <Table className="dynamic-table-area">
            <Scrollbars autoHeight={isFirefox} autoHeightMin={isFirefox ? 600 : 0}>
              <TableRow>
                {propsIsSelectable && propsOnSelectItem && (
                  <TableCell headerCell stickyCol singleLine>
                    {propsOnSelectAllItems ? (
                      <Checkbox
                        size="small"
                        checked={pageAllSelected}
                        indeterminate={pageSomeSelected && !pageAllSelected}
                        onChange={() => propsOnSelectAllItems(items, !pageAllSelected)}
                      />
                    ) : (
                      <Check />
                    )}
                  </TableCell>
                )}

                {props.fixedColumn && (
                  <TableCell
                    headerCell
                    stickyCol
                    isDesc={
                      orderColumns.current.length > 0 &&
                      orderColumns.current[0].columnName === props.fixedColumn.columnName &&
                      orderColumns.current[0].sortType === 'DESC'
                    }
                    onClick={sort(props.fixedColumn)}
                    singleLine
                  >
                    <Schedule style={{ display: 'block', margin: 'auto' }} />
                  </TableCell>
                )}

                {visibleColumns.map((visibleColumn, index: number) => (
                  <TableCell
                    key={index}
                    headerCell
                    isDesc={
                      orderColumns.current.length > 0 &&
                      orderColumns.current[0].columnName === visibleColumn.columnName &&
                      orderColumns.current[0].sortType === 'DESC'
                    }
                    onClick={sort(visibleColumn)}
                    singleLine
                  >
                    {visibleColumn.caption}
                  </TableCell>
                ))}

                {(props.onDelete || props.onRestore) && (
                  <a href="/#" className="table__cell cell cell--header cell--single-line cell--no-arrow">
                    &nbsp;
                  </a>
                )}

                {props.onDownload && (
                  <a href="/#" className="table__cell cell cell--header cell--single-line cell--no-arrow">
                    &nbsp;
                  </a>
                )}

                {props.onEdit && (
                  <a href="/#" className="table__cell cell cell--header cell--single-line cell--no-arrow">
                    &nbsp;
                  </a>
                )}

                {(props.onAdd || props.onRemove) && (
                  <a href="/#" className="table__cell cell cell--header cell--single-line cell--no-arrow">
                    <p className="cell__text cell__text--header">Добавлен в отчет</p>
                  </a>
                )}

                {props.onCopyLink && (
                  <a href="/#" className="table__cell cell cell--header cell--single-line cell--no-arrow">
                    &nbsp;
                  </a>
                )}

                {actionColumnCaptions.map((caption, actionIndex) => {
                  return (
                    <a
                      key={`actions-header-${caption ?? ''}-${actionIndex}`}
                      href="/#"
                      className="table__cell cell cell--header cell--single-line cell--no-arrow"
                    >
                      {caption ? <p className="cell__text cell__text--header">{caption}</p> : <>&nbsp;</>}
                    </a>
                  );
                })}
              </TableRow>

              {(pending || props.updating) && (
                <Box className="dynamic-table-loader">
                  <CircularProgress />
                </Box>
              )}

              {!pending &&
                !props.updating &&
                items.length > 0 &&
                items.map((item: any, itemIndex: number) => (
                  <TableRow
                    key={itemIndex}
                    onClick={!!props.onClick ? () => props.onClick!(item, itemIndex) : undefined}
                    link={!!props.setItemLink ? props.setItemLink(item) : null}
                    className={!!props.rowClassName ? props.rowClassName(item) : undefined}
                  >
                    {propsIsSelectable && propsOnSelectItem && (
                      <TableCell
                        className="cell--single-line"
                        onClick={isRowSelectable(item) ? () => propsOnSelectItem(item) : undefined}
                      >
                        {renderSelectionIcon(item)}
                      </TableCell>
                    )}

                    {props.fixedColumn && (
                      <TableCell stickyCol singleLine>
                        {props.fixedColumn.columnName === 'restOfDay' ? (
                          <div
                            className={classNames('cell__text cell__text--colored cell__text--center-text', {
                              'cell__text--almost-expired': item[props.fixedColumn.columnName] < 3,
                              'cell__text--expired': item[props.fixedColumn.columnName] < 0,
                            })}
                          >
                            {item[props.fixedColumn.columnName]}
                          </div>
                        ) : (
                          <div className="cell__text cell__text--colored cell__text--center-text">
                            {item[props.fixedColumn.columnName]}
                          </div>
                        )}
                      </TableCell>
                    )}

                    {visibleColumns.map((visibleColumn: IAvailableColumnModel, columnIndex: number) => (
                      <TableCell key={columnIndex} singleLine>
                        {['Timestamp', 'LocalDateTime', 'Date'].includes(visibleColumn.type) ? (
                          <div className="cell__text">
                            {formatTimestamp(item[visibleColumn.columnName], visibleColumn.type)}
                          </div>
                        ) : isNumericType(visibleColumn.type) ? (
                          <div className="cell__text" style={{ justifyContent: 'flex-end' }}>
                            {formatNumber(item[visibleColumn.columnName])}
                          </div>
                        ) : (
                          !['В работе', 'В ожидании', 'Выполнено', 'Закрыто', 'Отменена клиентом'].includes(
                            item[visibleColumn.columnName],
                          ) &&
                          visibleColumn.columnName !== 'restOfDay' &&
                          !PHOTO_COLUMNS.has(visibleColumn.columnName) && (
                            <div
                              className={classNames(
                                `cell__text ${
                                  [
                                    'areaNames',
                                    'assigneePositionNames',
                                    'comment',
                                    'defectActBrandName',
                                    'defectActBrandNames',
                                    'defectActWorkGuaranteeName',
                                    'defectActWorkGuaranteeNames',
                                    'defectReasonName',
                                    'delayReasonNames',
                                    'description',
                                    'descRu',
                                    'emails',
                                    'equipmentNames',
                                    'fieldName',
                                    'indicatorName',
                                    'lastComment',
                                    'names',
                                    'nameWork',
                                    'newValue',
                                    'note',
                                    'noteName',
                                    'oldValue',
                                    'positionNames',
                                    'realEstateNames',
                                    'serviceOrgAreaNames',
                                    'serviceOrgNames',
                                    'url',
                                    'workTypeNames',
                                    'workName',
                                    'workNames',
                                  ].includes(visibleColumn.columnName)
                                    ? ' text-wrap'
                                    : ''
                                }`,
                              )}
                            >
                              {visibleColumn.type === 'Boolean'
                                ? getBooleanValues(item[visibleColumn.columnName])
                                : Array.isArray(item[visibleColumn.columnName])
                                  ? item[visibleColumn.columnName].join(', ')
                                  : item[visibleColumn.columnName]}
                            </div>
                          )
                        )}

                        {PHOTO_COLUMNS.has(visibleColumn.columnName) && (
                          <div
                            className="cell__text text-wrap"
                            onClick={(event) => {
                              event.persist();
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                          >
                            <FileList
                              files={toPhotoKeys(item[visibleColumn.columnName])}
                              editable={false}
                              width={90}
                              height={90}
                              onChange={() => {}}
                            />
                          </div>
                        )}

                        {visibleColumn.columnName === 'restOfDay' && (
                          <div
                            className={classNames('cell__text cell__text--colored cell__text--center-text', {
                              'cell__text--almost-expired': item[visibleColumn.columnName] < 3,
                              'cell__text--expired': item[visibleColumn.columnName] < 0,
                            })}
                          >
                            {item[visibleColumn.columnName]}
                          </div>
                        )}

                        {item[visibleColumn.columnName] === 'В работе' && (
                          <div className="cell__text cell__text--work cell__text--colored">
                            {item[visibleColumn.columnName]}
                          </div>
                        )}

                        {item[visibleColumn.columnName] === 'В ожидании' && (
                          <div className="cell__text cell__text--pending cell__text--colored">
                            {item[visibleColumn.columnName]}
                          </div>
                        )}

                        {item[visibleColumn.columnName] === 'Выполнено' && (
                          <div className="cell__text cell__text--performed cell__text--colored">
                            {item[visibleColumn.columnName]}
                          </div>
                        )}

                        {(item[visibleColumn.columnName] === 'Закрыто' ||
                          item[visibleColumn.columnName] === 'Отменена клиентом') && (
                          <div
                            className={classNames('cell__text', {
                              'cell__text--closed cell__text--colored': filterConfigName !== 'doneApps-config',
                            })}
                          >
                            {item[visibleColumn.columnName]}
                          </div>
                        )}
                      </TableCell>
                    ))}

                    {(props.onAdd || props.onRemove) && (
                      <TableCell singleLine style={{ padding: 0 }}>
                        <IconButton
                          size={'small'}
                          className="download-button"
                          onClick={(event) => {
                            if (!!item['isForReport']) {
                              if (!!props.onRemove) {
                                event.persist();
                                event.preventDefault();
                                event.stopPropagation();
                                props.onRemove(item, itemIndex, event);
                              }
                            } else {
                              if (!!props.onAdd) {
                                event.persist();
                                event.preventDefault();
                                event.stopPropagation();
                                props.onAdd(item, itemIndex, event);
                              }
                            }
                          }}
                        >
                          {!!item['isForReport'] ? <CheckCircle /> : <RadioButtonUnchecked />}
                        </IconButton>
                      </TableCell>
                    )}

                    {(props.onDelete || props.onRestore) &&
                      (propsIsEditable !== undefined ? propsIsEditable : true) && (
                        <TableCell singleLine>
                          <IconButton
                            onClick={(event) => {
                              if (!!item['isDel'] || !!item['isDeleted']) {
                                if (!!props.onRestore) {
                                  event.persist();
                                  event.preventDefault();
                                  event.stopPropagation();
                                  props.onRestore(item, itemIndex, event);
                                }
                              } else {
                                if (!!props.onDelete) {
                                  event.persist();
                                  event.preventDefault();
                                  event.stopPropagation();
                                  props.onDelete(item, itemIndex, event);
                                }
                              }
                            }}
                          >
                            {!!item['isDel'] || !!item['isDeleted'] ? <SettingsBackupRestore /> : <Delete />}
                          </IconButton>
                        </TableCell>
                      )}

                    {props.onDownload && !props.onDownloadRender && (
                      <TableCell singleLine>
                        <IconButton
                          onClick={(event) => {
                            if (!!props.onDownload) {
                              event.persist();
                              event.preventDefault();
                              event.stopPropagation();
                              props.onDownload(item, itemIndex, event);
                            }
                          }}
                        >
                          <Download />
                        </IconButton>
                      </TableCell>
                    )}

                    {props.onDownloadRender && props.onDownload && (
                      <TableCell singleLine>
                        <div
                          onClick={(event) => {
                            if (!!props.onDownload) {
                              event.persist();
                              event.preventDefault();
                              event.stopPropagation();
                              props.onDownload(item, itemIndex);
                            }
                          }}
                        >
                          {props.onDownloadRender()}
                        </div>
                      </TableCell>
                    )}

                    {props.onEdit && (propsIsEditable !== undefined ? propsIsEditable : true) && (
                      <TableCell singleLine>
                        {!item.isDeleted && ( // Show edit button only if position not deleted
                          <IconButton
                            onClick={(event) => {
                              if (!!props.onEdit) {
                                event.persist();
                                event.preventDefault();
                                event.stopPropagation();
                                props.onEdit(item, itemIndex);
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </TableCell>
                    )}

                    {props.onCopyLink && (
                      <TableCell>
                        <ButtonGroup>
                          {props.onExtendPeriod && (
                            <Button
                              onClick={(event) => {
                                if (!!props.onExtendPeriod) {
                                  event.persist();
                                  event.preventDefault();
                                  event.stopPropagation();

                                  props.onExtendPeriod(item);
                                }
                              }}
                            >
                              Продлить срок
                            </Button>
                          )}

                          <Button
                            onClick={(event) => {
                              if (!!props.onCopyLink) {
                                event.persist();
                                event.preventDefault();
                                event.stopPropagation();

                                props.onCopyLink({ item, event });
                              }
                            }}
                          >
                            Скопировать ссылку
                          </Button>
                        </ButtonGroup>
                      </TableCell>
                    )}

                    {props.actions &&
                      props.actions(item, itemIndex)?.map(
                        ({ icon, label, action, display = true, IconButtonProps }) =>
                          display && (
                            <TableCell singleLine key={label + itemIndex}>
                              <Tooltip title={label}>
                                <IconButton
                                  aria-label={label}
                                  onClick={(event) => {
                                    event.persist();
                                    event.preventDefault();
                                    event.stopPropagation();

                                    action(event);
                                  }}
                                  {...IconButtonProps}
                                >
                                  {icon}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          ),
                      )}
                  </TableRow>
                ))}
            </Scrollbars>
          </Table>
        </Grid>

        <Grid item container justifyContent="space-between" xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 16 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 8 }}>
              <IconButton disabled={pageNo === 1} onClick={() => setPageNo(pageNo - 1)}>
                <ArrowBack />
              </IconButton>

              <IconButton onClick={() => setPageNo(pageNo + 1)} disabled={pageSize !== items.length}>
                <ArrowForward />
              </IconButton>
            </Box>

            <Typography>Страница: {pageNo}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 8 }}>
              <Typography component="span" style={{ margin: '0 10px 0 0' }}>
                Кол-во строк:
              </Typography>

              <Select value={pageSize} onChange={handleChange} displayEmpty>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={70}>70</MenuItem>
                <MenuItem value={80}>80</MenuItem>
                <MenuItem value={90}>90</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </Box>

            {props.listCountUrl &&
              (listCount === null ? (
                <Button disabled={pending} variant="outlined" onClick={getListCount}>
                  Показать количество
                </Button>
              ) : (
                <Typography>Всего: {listCount}</Typography>
              ))}

            {props.listExcelUrl && canDownloadExcel && (
              <Tooltip title="Скачать отчет Excel">
                <IconButton onClick={exportTable2Excel} disabled={excelFileDownloading}>
                  <ExcelIcon style={{ color: excelFileDownloading ? '#9ea19e' : '#3C763D' }} />
                </IconButton>
              </Tooltip>
            )}

            {props.listPDFUrl && (
              <Tooltip title="Скачать отчет PDF">
                <IconButton onClick={exportTable2PDF} disabled={PDFFileDownloading}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
      </Grid>

      <VisibilityDialog
        open={columnVisibilityDialogOpen}
        defaultValues={props.defaultValues}
        visibleItems={visibleColumns}
        hideColumns={props.hideColumns}
        groupColumns={availableGroupColumns}
        onSave={saveVisibleColumns}
        onClose={closeColumnVisibilityDialog}
      />

      <VisibilityDialog
        useFilterCriteria
        open={filterVisibilityDialogOpen}
        defaultValues={props.defaultValues}
        visibleItems={visibleFilters}
        hideFilters={props.hideFilters}
        groupColumns={availableGroupColumns}
        onSave={saveFilterColumns}
        onClose={closeFilterVisibilityDialog}
      />
    </>
  );
};

export default DynamicTable;
